import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from '../api/client';

const AuthContext = createContext(null);

// Role constants — keep in sync with backend/models/user.py UserRole
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  DEPT_MANAGER: 'dept_manager',
  DEPT_STAFF: 'dept_staff',
  CALL_CENTER: 'call_center',
};

export const ROLE_LABELS = {
  super_admin: 'Admin',
  dept_manager: 'Staff',
  dept_staff: 'Staff',
  call_center: 'Staff',
  // legacy values that may briefly appear before the backend migration runs
  admin: 'Admin',
  user: 'Staff',
};

export const ROLE_BADGE_COLOR = {
  super_admin: 'bg-purple-100 text-purple-700',
  dept_manager: 'bg-indigo-100 text-indigo-700',
  dept_staff: 'bg-blue-100 text-blue-700',
  call_center: 'bg-amber-100 text-amber-700',
  admin: 'bg-purple-100 text-purple-700',
  user: 'bg-indigo-100 text-indigo-700',
};

// ── Feature-permissions layer ──────────────────────────────────────────
// Keep in sync with backend/utils/feature_permissions.py
export const ALL_FEATURES = [
  'calendar', 'teams', 'call_monitor',
  'hr_module', 'finance_module', 'accounts_module', 'ai_projects', 'admin_panel',
];

export const FEATURE_LABELS = {
  calendar:        { label: 'Calendar',           desc: 'Calendar page' },
  teams:           { label: 'Teams Mentions',     desc: 'Teams Mentions page' },
  call_monitor:    { label: 'Call Monitor',       desc: 'Call Monitor page' },
  hr_module:       { label: 'HR Module',          desc: 'HR department access' },
  finance_module:  { label: 'Finance Module',     desc: 'Finance department access' },
  accounts_module: { label: 'Accounts Module',    desc: 'Accounts department access' },
  ai_projects:     { label: 'AI Chat & Projects', desc: 'AI chat and projects' },
  admin_panel:     { label: 'Admin Panel',        desc: 'Admin Panel access' },
};

// ── Module-permissions layer (two-tier model) ──────────────────────────
// Keep in sync with backend/utils/module_permissions.py
export const MODULES = [
  { key: 'hr_cv_screening',        label: 'HR — CV Screening' },
  { key: 'hr_form_builder',        label: 'HR — Form Builder' },
  { key: 'hr_reports',             label: 'HR — Reports' },
  { key: 'hr_interview_scheduling',label: 'HR — Interview Scheduling' },
  { key: 'accounts',               label: 'Accounts' },
  { key: 'finance',                label: 'Finance' },
  { key: 'calendar',               label: 'Calendar' },
  { key: 'call_monitor',           label: 'Call Monitor' },
  { key: 'teams_mentions',         label: 'Teams Mentions' },
];

export const MODULE_ACTIONS = ['view', 'add', 'edit', 'delete'];

// Map a department name to its gating feature (mirrors backend dept_module).
export function deptFeature(name) {
  const n = (name || '').toLowerCase();
  if (n.includes('hr') || n.includes('human resource')) return 'hr_module';
  if (n.includes('finance')) return 'finance_module';
  if (n.includes('account')) return 'accounts_module';
  return null;
}

// Role-based default feature set (mirrors backend default_features).
export function defaultFeatures(role, departmentName) {
  if (role === ROLES.SUPER_ADMIN || role === 'admin') return [...ALL_FEATURES];
  if (role === ROLES.CALL_CENTER) return ['call_monitor'];
  if (role === ROLES.DEPT_MANAGER || role === 'user') {
    return ['hr_module', 'finance_module', 'accounts_module', 'ai_projects'];
  }
  if (role === ROLES.DEPT_STAFF) {
    const feats = ['ai_projects'];
    const m = deptFeature(departmentName);
    if (m) feats.push(m);
    return feats;
  }
  return [];
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const res = await authAPI.me();
      setUser(res.data);
    } catch {
      setUser(null);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) fetchMe();
    else setLoading(false);
  }, [fetchMe]);

  const login = async (email, password) => {
    const res = await authAPI.login(email, password);
    localStorage.setItem('token', res.data.access_token);
    await fetchMe();
  };

  const logout = () => {
    // Best-effort logout audit (token still present for the request header).
    authAPI.logout().catch(() => {});
    localStorage.removeItem('token');
    setUser(null);
  };

  const value = useMemo(() => {
    const role = user?.role;
    const isSuperAdmin = role === ROLES.SUPER_ADMIN || role === 'admin';
    const isCallCenter = role === ROLES.CALL_CENTER;
    const isDeptManager = role === ROLES.DEPT_MANAGER || role === 'user';
    const isDeptStaff = role === ROLES.DEPT_STAFF;
    const permissions = user?.permissions || [];
    const featurePermissions = user?.feature_permissions || [];
    const modulePermissions = user?.module_permissions || {};

    // Super admins implicitly have every feature (matches backend override).
    const hasFeature = (feature) => isSuperAdmin || featurePermissions.includes(feature);

    // Module-CRUD check (two-tier model). Admin bypasses; Staff checked vs grid.
    const canModule = (moduleKey, action = 'view') =>
      isSuperAdmin || Boolean(modulePermissions?.[moduleKey]?.[action]);

    const permFor = (deptId) => {
      if (isSuperAdmin) {
        return { can_create: true, can_read: true, can_update: true, can_delete: true };
      }
      return permissions.find((p) => p.department_id === Number(deptId));
    };
    const can = (deptId, action) => {
      if (isSuperAdmin) return true;
      const p = permFor(deptId);
      if (!p) return false;
      return Boolean(p[`can_${action}`]);
    };

    return {
      user,
      loading,
      login,
      logout,
      fetchMe,
      // backward-compat
      isAdmin: isSuperAdmin,
      // new helpers
      role,
      isSuperAdmin,
      isCallCenter,
      isDeptManager,
      isDeptStaff,
      permissions,
      featurePermissions,
      hasFeature,
      // two-tier helpers
      isStaff: !isSuperAdmin,
      modulePermissions,
      canModule,
      permFor,
      canRead: (deptId) => can(deptId, 'read'),
      canCreate: (deptId) => can(deptId, 'create'),
      canUpdate: (deptId) => can(deptId, 'update'),
      canDelete: (deptId) => can(deptId, 'delete'),
    };
  }, [user, loading, fetchMe]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
